// =================================================================
// 				SEPET YÖNETİMİ CONTEXT - REACT VERSİYONU
// =================================================================

import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { STORAGE_KEYS } from '../config/constants';
import { logger } from '../utils/logger';

// Sepet item interface'i
export interface CartItem {
    id: string;
    name: string;
    price: number;
    quantity: number;
    unit: string;
    image?: string;
    category?: string;
    description?: string;
    maxQuantity?: number;
}

// Sepet state interface'i
export interface CartState {
    items: CartItem[];
    totalItems: number;
    totalPrice: number;
    isLoading: boolean;
    error: string | null;
}

// Action türleri
type CartAction =
    | { type: 'ADD_TO_CART'; payload: Omit<CartItem, 'quantity'> & { quantity?: number } }
    | { type: 'REMOVE_FROM_CART'; payload: string }
    | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
    | { type: 'CLEAR_CART' }
    | { type: 'LOAD_CART'; payload: CartItem[] }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null };

// Context interface'i
interface CartContextType {
    cart: CartState;
    addToCart: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
    removeFromCart: (itemId: string) => void;
    updateQuantity: (itemId: string, quantity: number) => void;
    clearCart: () => void;
    getItemQuantity: (itemId: string) => number;
    isInCart: (itemId: string) => boolean;
    getTotalWeight: () => number;
    validateCart: () => { isValid: boolean; errors: string[] };
}

// Başlangıç state'i
const initialState: CartState = {
    items: [],
    totalItems: 0,
    totalPrice: 0,
    isLoading: false,
    error: null
};

// Reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
    switch (action.type) {
        case 'ADD_TO_CART': {
            const existingItemIndex = state.items.findIndex(item => item.id === action.payload.id);
            const normalizedMax = typeof action.payload.maxQuantity === 'number'
                ? Math.max(0, Math.floor(action.payload.maxQuantity))
                : undefined;
            if (normalizedMax === 0) {
                return state;
            }
            const fallbackMax = normalizedMax ?? 999;
            const requestedQuantity = action.payload.quantity && action.payload.quantity > 0 ? action.payload.quantity : 1;
            const newQuantity = Math.min(requestedQuantity, fallbackMax);
            
            let newItems: CartItem[];
            
            if (existingItemIndex >= 0) {
                // Mevcut ürünü güncelle
                newItems = state.items.map((item, index) => {
                    if (index === existingItemIndex) {
                        const maxQuantity = normalizedMax ?? item.maxQuantity ?? 999;
                        const updatedQuantity = Math.min(item.quantity + newQuantity, maxQuantity);
                        return {
                            ...item,
                            quantity: updatedQuantity,
                            maxQuantity,
                            description: action.payload.description ?? item.description
                        };
                    }
                    return item;
                });
            } else {
                // Yeni ürün ekle
                const newItem: CartItem = {
                    ...action.payload,
                    quantity: newQuantity,
                    maxQuantity: normalizedMax
                };
                newItems = [...state.items, newItem];
            }
            
            const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
            const totalPrice = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            
            return {
                ...state,
                items: newItems,
                totalItems,
                totalPrice,
                error: null
            };
        }
        
        case 'REMOVE_FROM_CART': {
            const newItems = state.items.filter(item => item.id !== action.payload);
            const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
            const totalPrice = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            
            return {
                ...state,
                items: newItems,
                totalItems,
                totalPrice
            };
        }
        
        case 'UPDATE_QUANTITY': {
            const { id, quantity } = action.payload;
            
            if (quantity <= 0) {
                // Miktarı 0 veya negatifse ürünü kaldır
                return cartReducer(state, { type: 'REMOVE_FROM_CART', payload: id });
            }
            
            const newItems = state.items.map(item => {
                if (item.id === id) {
                    const maxQuantity = item.maxQuantity || 999;
                    return {
                        ...item,
                        quantity: Math.min(quantity, maxQuantity)
                    };
                }
                return item;
            });
            
            const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
            const totalPrice = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            
            return {
                ...state,
                items: newItems,
                totalItems,
                totalPrice
            };
        }
        
        case 'CLEAR_CART':
            return {
                ...state,
                items: [],
                totalItems: 0,
                totalPrice: 0,
                error: null
            };
        
        case 'LOAD_CART': {
            const items = action.payload;
            const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
            const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            
            return {
                ...state,
                items,
                totalItems,
                totalPrice,
                isLoading: false
            };
        }
        
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: action.payload
            };
        
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload,
                isLoading: false
            };
        
        default:
            return state;
    }
};

// Context oluştur
const CartContext = createContext<CartContextType | undefined>(undefined);

// Provider component
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cart, dispatch] = useReducer(cartReducer, initialState);
    
    // Local storage'dan sepeti yükle
    useEffect(() => {
        try {
            const savedCart = localStorage.getItem(STORAGE_KEYS.CART_ITEMS);
            if (savedCart) {
                const parsedCart = JSON.parse(savedCart);
                dispatch({ type: 'LOAD_CART', payload: parsedCart });
            }
        } catch (error) {
            logger.error('Sepet yüklenirken hata:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Sepet bilgileri yüklenemedi' });
        }
    }, []);
    
    // Sepet değişikliklerini local storage'a kaydet
    useEffect(() => {
        if (cart.items.length > 0) {
            try {
                localStorage.setItem(STORAGE_KEYS.CART_ITEMS, JSON.stringify(cart.items));
            } catch (error) {
                logger.error('Sepet kaydedilirken hata:', error);
            }
        } else {
            localStorage.removeItem(STORAGE_KEYS.CART_ITEMS);
        }
    }, [cart.items]);
    
    // Sepete ürün ekle
    const addToCart = useCallback((item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
        dispatch({ type: 'ADD_TO_CART', payload: item });
    }, []);
    
    // Sepetten ürün çıkar
    const removeFromCart = useCallback((itemId: string) => {
        dispatch({ type: 'REMOVE_FROM_CART', payload: itemId });
    }, []);
    
    // Ürün miktarını güncelle
    const updateQuantity = useCallback((itemId: string, quantity: number) => {
        dispatch({ type: 'UPDATE_QUANTITY', payload: { id: itemId, quantity } });
    }, []);
    
    // Sepeti temizle
    const clearCart = useCallback(() => {
        dispatch({ type: 'CLEAR_CART' });
    }, []);
    
    // Ürün miktarını al
    const getItemQuantity = useCallback((itemId: string): number => {
        const item = cart.items.find(item => item.id === itemId);
        return item ? item.quantity : 0;
    }, [cart.items]);
    
    // Ürünün sepette olup olmadığını kontrol et
    const isInCart = useCallback((itemId: string): boolean => {
        return cart.items.some(item => item.id === itemId);
    }, [cart.items]);
    
    // Toplam ağırlığı hesapla (kargo hesaplaması için)
    const getTotalWeight = useCallback((): number => {
        return cart.items.reduce((total, item) => {
            // Varsayılan ağırlık: 1kg per item
            const weight = 1; // Bu değer ürün datasından alınabilir
            return total + (weight * item.quantity);
        }, 0);
    }, [cart.items]);
    
    // Sepeti doğrula
    const validateCart = useCallback((): { isValid: boolean; errors: string[] } => {
        const errors: string[] = [];
        
        if (cart.items.length === 0) {
            errors.push('Sepetinizde ürün bulunmuyor');
        }
        
        cart.items.forEach(item => {
            if (item.quantity <= 0) {
                errors.push(`${item.name} için geçersiz miktar`);
            }
            
            if (item.maxQuantity && item.quantity > item.maxQuantity) {
                errors.push(`${item.name} için maksimum ${item.maxQuantity} adet seçebilirsiniz`);
            }
            
            if (item.price <= 0) {
                errors.push(`${item.name} için geçersiz fiyat`);
            }
        });
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }, [cart.items]);
    
    const contextValue = useMemo<CartContextType>(() => ({
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getItemQuantity,
        isInCart,
        getTotalWeight,
        validateCart
    }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, getItemQuantity, isInCart, getTotalWeight, validateCart]);
    
    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    );
};

// Hook untuk sepet context'ini kullanma
export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export default CartContext;
